// v4.1 B1 (overnight Wave 5): Shorts from a finished version, in the Publish fold. The api suggests 1-3 moments (whole
// lines, the strongest openings, never the end card); tick the ones to keep, pick the framing and "Cut" makes 9:16
// videos of them (free; ffmpeg on the version's own file). GET/POST /api/projects/:id/shorts (api/src/v4/shorts.ts).
import { useEffect, useState } from "react";
import { call } from "../api";
import { toast } from "../store";
import { useLoad } from "./bits";

export type ShortSuggestion = { startS: number; endS: number; scenes: number[]; hook: string; why: string; score: number };
export type ShortCut = { id: string; version: number | null; startS: number; endS: number; framing: "fit" | "crop"; status: "queued" | "running" | "done" | "failed"; error: string | null; url: string | null; downloadUrl: string | null; hook: string | null };
export type ShortsOverview = { version: number; durationS: number; aspect: string; suggestions: ShortSuggestion[]; cuts: ShortCut[]; perDay: number; left: number };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

export function normCut(v: unknown): ShortCut | null {
  if (!isObj(v) || !str(v.id)) return null;
  return {
    id: str(v.id),
    version: typeof v.version === "number" ? v.version : null,
    startS: num(v.startS),
    endS: num(v.endS),
    framing: v.framing === "crop" ? "crop" : "fit",
    status: (["queued", "running", "done", "failed"].includes(str(v.status)) ? str(v.status) : "failed") as ShortCut["status"],
    error: str(v.error) || null,
    url: str(v.url) || null,
    downloadUrl: str(v.downloadUrl) || null,
    hook: str(v.hook) || null,
  };
}

export function normOverview(v: unknown): ShortsOverview | null {
  if (!isObj(v) || typeof v.version !== "number") return null;
  return {
    version: v.version,
    durationS: num(v.durationS),
    aspect: str(v.aspect) || "16:9",
    suggestions: (Array.isArray(v.suggestions) ? v.suggestions : []).filter(isObj).map((s) => ({ startS: num(s.startS), endS: num(s.endS), scenes: (Array.isArray(s.scenes) ? s.scenes : []).map(num), hook: str(s.hook), why: str(s.why), score: num(s.score) })),
    cuts: (Array.isArray(v.cuts) ? v.cuts : []).map(normCut).filter((c): c is ShortCut => !!c),
    perDay: num(v.perDay),
    left: num(v.left),
  };
}

export const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function ShortsPanel({ projectId, version }: { projectId: string; version: number }) {
  const { data, reload } = useLoad(async () => normOverview(await call("GET", `projects/${encodeURIComponent(projectId)}/shorts?v=${version}`).catch(() => null)), [projectId, version]);
  const [picked, setPicked] = useState<Set<number> | null>(null);
  const [framing, setFraming] = useState<"fit" | "crop" | null>(null);
  const [busy, setBusy] = useState(false);
  const working = !!data?.cuts.some((c) => c.status === "queued" || c.status === "running");
  useEffect(() => {
    if (!working) return;
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [working, reload]);
  if (!data) return null;
  const sel = picked ?? new Set(data.suggestions.map((_, i) => i));
  const frame = framing ?? (data.aspect === "9:16" ? "crop" : "fit");
  const cut = async () => {
    setBusy(true);
    try {
      const moments = data.suggestions.filter((_, i) => sel.has(i)).map((s) => ({ startS: s.startS, endS: s.endS }));
      await call("POST", `projects/${encodeURIComponent(projectId)}/shorts`, { version, moments, framing: frame });
      toast(`Cutting ${moments.length} Short${moments.length === 1 ? "" : "s"}. They'll show here in a minute.`, "good");
      setPicked(null);
      reload();
    } catch (err) {
      toast(`Couldn't cut the Shorts. ${(err as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pk-row shorts">
      <div className="pk-head">
        <span className="field-label">Shorts (9:16)</span>
        <span className="muted">Free · {data.left} left today</span>
      </div>
      {data.suggestions.length === 0 ? (
        <p className="muted">This version is too short to cut Shorts from.</p>
      ) : (
        <>
          <ul className="shorts-picks">
            {data.suggestions.map((s, i) => (
              <li key={`${s.startS}-${s.endS}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={sel.has(i)}
                    onChange={() => {
                      const next = new Set(sel);
                      if (next.has(i)) next.delete(i);
                      else next.add(i);
                      setPicked(next);
                    }}
                  />{" "}
                  <strong>
                    {clock(s.startS)}–{clock(s.endS)}
                  </strong>{" "}
                  <span className="muted">“{s.hook}”</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="shorts-bar">
            <span className="shorts-framing" role="radiogroup" aria-label="Framing">
              <button type="button" role="radio" aria-checked={frame === "fit"} className={`btn btn-small ${frame === "fit" ? "btn-primary" : "btn-quiet"}`} onClick={() => setFraming("fit")} title="The whole picture over a blurred fill: nothing is cut off">
                Whole picture
              </button>
              <button type="button" role="radio" aria-checked={frame === "crop"} className={`btn btn-small ${frame === "crop" ? "btn-primary" : "btn-quiet"}`} onClick={() => setFraming("crop")} title="Fill the screen: the middle of the picture, sides cut off">
                Fill (crop)
              </button>
            </span>
            <button type="button" className="btn btn-primary btn-small" data-action="shorts.cut" disabled={busy || !sel.size || data.left < sel.size} onClick={() => void cut()}>
              {busy ? "Starting" : `Cut ${sel.size} Short${sel.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      )}
      {data.cuts.length > 0 && (
        <ul className="shorts-cuts">
          {data.cuts.slice(0, 6).map((c) => (
            <li key={c.id} className={`short-cut short-${c.status}`}>
              {c.status === "done" && c.url ? <video src={c.url} controls playsInline preload="metadata" className="short-video" /> : <div className="short-video short-wait">{c.status === "failed" ? "Failed" : "Cutting…"}</div>}
              <p className="short-meta">
                v{c.version ?? "?"} · {clock(c.startS)}–{clock(c.endS)}
                {c.status === "failed" && c.error ? ` · ${c.error}` : ""}
              </p>
              {c.downloadUrl && (
                <a className="btn btn-quiet btn-small" href={c.downloadUrl} download>
                  Download
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
